import { Alert, PrimaryButton } from '@designsforhealth/dfh-react-components'
import { CircularProgress } from '@material-ui/core'
import { Subscribe } from '@react-rxjs/core'
import React, { useEffect, useMemo, useState } from 'react'
import { DefaultValues, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { combineLatest, first, firstValueFrom, map, skipWhile, takeWhile, tap } from 'rxjs'
import styled, { DefaultTheme, useTheme } from 'styled-components'

import {
  SanityDispensaryDocument,
  SanityHeroImageOptionDocument,
  SanityHeroTextOptionDocument,
} from '../../../../../../groq/sanity-dispensaries/types'
import withDispensaryAccount, {
  WithDispensaryAccountFC,
} from '../../../../../../hoc/withDispensaryAccount'
import { useLogAndCaptureLeftResult } from '../../../../../../hooks/useLogAndCaptureLeftResult'
import { useResultWithLeftEffect } from '../../../../../../hooks/useResultWithLeftEffect'
import {
  sanityDispensaryGetOrCreate$,
  useSanityDispensaryGetOrCreate,
} from '../../../../../../lib/sanity-dispensaries/manage/dispensary'
import {
  dispensaryHeroImageOption$,
  useDispensaryHeroImageOption,
  useManagedHeroImageOptions,
} from '../../../../../../lib/sanity-dispensaries/manage/hero-image-options'
import {
  dispensaryHeroTextOption$,
  useDispensaryHeroTextOption,
  useManagedHeroTextOptions,
} from '../../../../../../lib/sanity-dispensaries/manage/hero-text-options'
import {
  sanityDispensaryMergedUpdate$,
  setMergedUpdateDispensaryInput,
  useSanityDispensaryMergedUpdate,
} from '../../../../../../lib/sanity-dispensaries/manage/merged-update'
import {
  constEmptyArray,
  constEmptyResult,
  constNull,
  constUndefined,
} from '../../../../../../utils/constant'
import { switchMapRight } from '../../../../../../utils/rx/operators'
import { EmptyObject } from '../../../../../../utils/types'
import ContactSupportMessage from '../../../../../ContactSupportMessage'
import ErrorRetrievingDataMessage from '../../../../../ErrorRetrievingDataMessage'
import Spacer from '../../../../../Spacer'
import Spinner from '../../../../../Spinner'
import HeroImageCreate from './HeroImageCreate'
import HeroImageSelect from './HeroImageSelect'
import HeroLivePreview from './HeroLivePreview'
import HeroTextCreate from './HeroTextCreate'
import HeroTextSelect, { checkTextOptionAfterReset } from './HeroTextSelect'

const StyledHeading = styled.h4`
  margin-top: 30px;
  margin-bottom: 15px;
`

const StyledHeaderWithButton = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  @media print {
    button {
      display: none;
    }
  }
  @media (min-width: 450px) {
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
    h3 {
      margin-bottom: 0;
    }
    button {
      display: block;
      margin-left: 15px;
    }
  }
`

const StyledTrailingButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-bottom: 3rem;
  @media (min-width: 450px) {
    align-items: flex-end;
  }
  @media print {
    button {
      display: none;
    }
  }
`

export interface DispensaryHeroFormValues {
  selectedImageOptionId: string
  selectedTextOptionId: string
  imageAssetRef: string
  textColor: string
  ctaBackgroundColor: string
  ctaTextColor: string
  text: string
  ctaText: string
  renderEntireImageAsCta: 'yes' | 'no'
}

const pluckData = <T,>({ data }: { data: T }) => data

const buildDefaultValues: (
  data: readonly [
    SanityDispensaryDocument,
    SanityHeroImageOptionDocument | null,
    SanityHeroTextOptionDocument | null,
  ],
  theme: DefaultTheme,
) => DefaultValues<DispensaryHeroFormValues> = (
  [dispensary, dispensaryHeroImageOption, dispensaryHeroTextOption],
  theme,
) => ({
  ctaBackgroundColor: dispensaryHeroImageOption?.ctaBackgroundColor ?? theme.colors.brand.orange,
  ctaText: dispensaryHeroTextOption?.ctaText ?? '',
  ctaTextColor: dispensaryHeroImageOption?.ctaTextColor ?? theme.colors.grayscale.white,
  imageAssetRef: dispensaryHeroImageOption?.image.asset._ref ?? '',
  // renderEntireImageAsCta is only an option for custom hero image options
  renderEntireImageAsCta:
    dispensaryHeroImageOption &&
    dispensary.homePage?.heroImage?._ref === dispensaryHeroImageOption._id &&
    dispensaryHeroImageOption.renderEntireImageAsCta
      ? 'yes'
      : 'no',
  selectedImageOptionId: dispensary.homePage?.heroImage?._ref ?? '',
  selectedTextOptionId: dispensary?.homePage?.heroText?._ref ?? '',
  text: dispensaryHeroTextOption?.text ?? '',
  textColor: dispensaryHeroImageOption?.textColor ?? theme.colors.grayscale.black,
})

const CustomizeHomePageReactive: React.FC = () => {
  const [sanityDispensaryLeft, sanityDispensary] = useResultWithLeftEffect(
    useSanityDispensaryGetOrCreate(),
    pluckData,
    constUndefined,
  )
  const [heroImageOptionsLeft, heroImageOptions] = useResultWithLeftEffect(
    useManagedHeroImageOptions(),
    pluckData,
    constEmptyArray,
  )
  const [heroTextOptionsLeft, heroTextOptions] = useResultWithLeftEffect(
    useManagedHeroTextOptions(),
    pluckData,
    constEmptyArray,
  )
  const [dispensaryHeroImageOptionLeft, dispensaryHeroImageOption] = useResultWithLeftEffect(
    useDispensaryHeroImageOption(),
    pluckData,
    constNull,
  )
  const [dispensaryHeroTextOptionLeft, dispensaryHeroTextOption] = useResultWithLeftEffect(
    useDispensaryHeroTextOption(),
    pluckData,
    constNull,
  )

  const { loading: dispensaryMergedUpdateLoading, result: dispensaryMergedUpdateResult } =
    useSanityDispensaryMergedUpdate()
  useLogAndCaptureLeftResult(dispensaryMergedUpdateResult ?? constEmptyResult())

  const theme = useTheme()

  const [initialDefaultValues] = useState<DefaultValues<DispensaryHeroFormValues>>(() => ({
    ctaBackgroundColor: theme.colors.brand.orange,
    ctaText: '',
    ctaTextColor: theme.colors.grayscale.white,
    imageAssetRef: '',
    renderEntireImageAsCta: 'no',
    selectedImageOptionId: '',
    selectedTextOptionId: '',
    text: '',
    textColor: theme.colors.grayscale.black,
  }))

  const methods = useForm<DispensaryHeroFormValues>({
    defaultValues: initialDefaultValues,
    shouldUnregister: false,
  })
  const {
    formState: { isDirty, isSubmitting },
    handleSubmit,
    reset,
    watch,
  } = methods

  const { renderEntireImageAsCta, selectedImageOptionId, selectedTextOptionId } = watch([
    'renderEntireImageAsCta',
    'selectedImageOptionId',
    'selectedTextOptionId',
  ])

  const managedHeroImageOptionRenderEntireAsCta = useMemo(() => {
    const managedHeroImageOption = heroImageOptions.find(
      (option) => option._id === selectedImageOptionId,
    )
    return !managedHeroImageOption
      ? undefined
      : managedHeroImageOption.renderEntireImageAsCta
        ? ('yes' as const)
        : ('no' as const)
  }, [heroImageOptions, selectedImageOptionId])

  useEffect(() => {
    const subscription = combineLatest([
      sanityDispensaryGetOrCreate$,
      dispensaryHeroImageOption$,
      dispensaryHeroTextOption$,
    ])
      .pipe(
        // sequence fetch result
        map(([a, b, c]) =>
          a._tag === 'Left'
            ? a
            : b._tag === 'Left'
              ? b
              : c._tag === 'Left'
                ? c
                : { _tag: 'Right' as const, data: [a.data, b.data, c.data] as const },
        ),
        switchMapRight(({ data }) =>
          sanityDispensaryMergedUpdate$.pipe(
            // once update begins, discontinue form reset until `data` changes
            takeWhile((mergedUpdateResult) => !mergedUpdateResult.loading),
            tap(() => {
              // update selections when Sanity documents change
              reset(buildDefaultValues(data, theme))
              checkTextOptionAfterReset()
            }),
          ),
        ),
      )
      .subscribe()
    return () => subscription.unsubscribe()
  }, [reset, theme])

  if (sanityDispensaryLeft ?? !sanityDispensary) {
    return (
      <div>
        <Alert type="error">
          An error occurred retrieving your dispensary customization, and we have been notified.{' '}
          <ContactSupportMessage />
        </Alert>
      </div>
    )
  }

  const otherLeft =
    heroImageOptionsLeft ??
    heroTextOptionsLeft ??
    dispensaryHeroImageOptionLeft ??
    dispensaryHeroTextOptionLeft

  const setNextUpdateInput = (data: DispensaryHeroFormValues) => {
    setMergedUpdateDispensaryInput({
      // upsert hero image option if custom option selected
      ...(data.selectedImageOptionId === (dispensaryHeroImageOption?._id || 'new') && {
        heroImageOption: {
          imageRef: data.imageAssetRef,
          textColor: data.textColor,
          ctaBackgroundColor: data.ctaBackgroundColor,
          ctaTextColor: data.ctaTextColor,
          renderEntireImageAsCta: data.renderEntireImageAsCta === 'yes',
        },
      }),
      // upsert hero text option if custom option selected
      ...(data.selectedTextOptionId === (dispensaryHeroTextOption?._id || 'new') && {
        heroTextOption: {
          text: data.text,
          ctaText: data.ctaText,
        },
      }),
      dispensary: {
        ...(data.selectedImageOptionId !== 'new' && { heroImageRef: data.selectedImageOptionId }),
        ...(data.selectedTextOptionId !== 'new' && { heroTextRef: data.selectedTextOptionId }),
      },
    })
  }

  const onSubmit: SubmitHandler<DispensaryHeroFormValues> = (
    formValues: DispensaryHeroFormValues,
  ): Promise<void> => {
    setNextUpdateInput(formValues)

    // raise error to react-hook-form if result === Left
    return firstValueFrom(
      sanityDispensaryMergedUpdate$.pipe(
        // ignore any value that matches an existing result
        skipWhile(({ result }) => result === dispensaryMergedUpdateResult),
        // find the first notification where loading is false
        first(({ loading }) => !loading),
        map(({ result }) => {
          if (!result) {
            throw new Error('Dispensary merged update result not found')
          }
          if (result._tag === 'Left') {
            throw result.error
          }
        }),
      ),
    )
  }

  const loading = dispensaryMergedUpdateLoading
  const submitButton = (
    <PrimaryButton type="submit" disabled={!isDirty || isSubmitting || loading}>
      {isSubmitting || loading ? (
        <CircularProgress size={20} color="inherit" />
      ) : isDirty ? (
        'Publish Changes'
      ) : (
        'No Changes to Publish'
      )}
    </PrimaryButton>
  )

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <StyledHeaderWithButton>
          <h3>Customize Home Page</h3>
          {submitButton}
        </StyledHeaderWithButton>
        {dispensaryMergedUpdateResult?._tag === 'Left' && (
          <Alert type="error">
            An error occurred updating your dispensary, and we have been notified.{' '}
            <ContactSupportMessage />
          </Alert>
        )}
        {!!otherLeft && (
          <Alert type="error">
            <ErrorRetrievingDataMessage />
          </Alert>
        )}
        <StyledHeading>Preview</StyledHeading>
        <HeroLivePreview
          imageOptionForSlug={dispensaryHeroImageOption}
          textOptionForSlug={dispensaryHeroTextOption}
          imageOptions={heroImageOptions}
          textOptions={heroTextOptions}
          renderEntireImageAsCta={
            // use `renderEntireImageAsCta` from managed hero image option if selected
            managedHeroImageOptionRenderEntireAsCta ?? renderEntireImageAsCta
          }
        />
        <StyledHeading>Select Banner Image</StyledHeading>
        <HeroImageSelect
          disabled={dispensaryMergedUpdateLoading}
          options={heroImageOptions}
          optionForSlug={dispensaryHeroImageOption}
        />
        {selectedImageOptionId === (dispensaryHeroImageOption?._id || 'new') && (
          <>
            <StyledHeading>Create Banner Image</StyledHeading>
            <HeroImageCreate />
          </>
        )}
        {managedHeroImageOptionRenderEntireAsCta !== 'yes' && (
          <>
            <StyledHeading>Select Banner Text</StyledHeading>
            <HeroTextSelect
              disabled={
                dispensaryMergedUpdateLoading ||
                (managedHeroImageOptionRenderEntireAsCta == null &&
                  renderEntireImageAsCta === 'yes')
              }
              options={heroTextOptions}
              optionForSlug={dispensaryHeroTextOption}
              renderEntireImageAsCta={
                // use `renderEntireImageAsCta` from managed hero image option if selected
                managedHeroImageOptionRenderEntireAsCta ?? renderEntireImageAsCta
              }
            />
            {selectedTextOptionId === (dispensaryHeroTextOption?._id || 'new') &&
              (managedHeroImageOptionRenderEntireAsCta != null ||
                renderEntireImageAsCta === 'no') && (
                <>
                  <StyledHeading>Create Banner Text</StyledHeading>
                  <HeroTextCreate optionForSlug={dispensaryHeroTextOption} />
                </>
              )}
          </>
        )}
        <Spacer size={3} />
        <StyledTrailingButtonContainer>{submitButton}</StyledTrailingButtonContainer>
      </form>
    </FormProvider>
  )
}

const CustomizeHomePage: WithDispensaryAccountFC<EmptyObject> = () => (
  <Subscribe fallback={<Spinner loading />}>
    <CustomizeHomePageReactive />
  </Subscribe>
)

export default withDispensaryAccount(CustomizeHomePage)
